import { useCallback, useEffect, useRef, useState } from 'react';

import { useHoldTicker } from './native-cabinet-shell';
import type {
  NativeBooking,
  NativeBookingApi,
  NativeEditProposal,
  NativeFormResponse,
} from './native-booking-api';
import {
  NativeVerticalForm,
  buildDocumentDraftPatch,
  buildDraftPatch,
  normalizeDraftTypes,
} from './native-booking-form-fields';
import { NativeReuseProfiles } from './native-reuse-profiles';
import type { NativeReuseContext } from './native-booking-api';
import {
  isNativeStaleConflict,
  isNativeValidationError,
  nativeFieldErrors,
  nativeProblemMessage,
  nativeValidationSummary,
} from './native-booking-problem';
import { NativeMonthCalendar } from './native-booking-month-calendar';
import {
  formatDayLongRu,
  formatEkbDateTime,
  formatHoldCountdown,
  monthKeyFromYmd,
  slotLocalTime,
  ymdInEkb,
} from './native-booking-presentation';
import { withNativePlateFormatChoice } from './native-plate-format-choice';
import { navigate, subscribePortalPath } from './path-router';

function readQueryParam(name: string): string | null {
  const params = new URLSearchParams(window.location.search);
  const id = params.get(name);
  return id === null || id.trim() === '' ? null : id.trim();
}

export function NativeBookingPage(props: {
  readonly api: NativeBookingApi;
  readonly catalogSites: readonly { id: string; display_name: string }[];
  readonly catalogEquipment: readonly { id: string; display_name: string }[];
}): React.ReactElement {
  // Live query sync — editMode must follow URL after apply/abandon (same /booking path).
  const [bookingQueryId, setBookingQueryId] = useState(() => readQueryParam('booking'));
  const [proposalQueryId, setProposalQueryId] = useState(() => readQueryParam('proposal'));
  const editMode = proposalQueryId !== null;

  const [siteId, setSiteId] = useState('');
  const [equipmentId, setEquipmentId] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => ymdInEkb(new Date()));
  const [viewMonth, setViewMonth] = useState(() => monthKeyFromYmd(ymdInEkb(new Date())));
  const [slots, setSlots] = useState<readonly { start_at: string; available: boolean }[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState<NativeBooking | null>(null);
  const [proposal, setProposal] = useState<NativeEditProposal | null>(null);
  const [form, setForm] = useState<NativeFormResponse | null>(null);
  const [reuseContext, setReuseContext] = useState<NativeReuseContext | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({});
  const [success, setSuccess] = useState<string | null>(null);
  const [staleHint, setStaleHint] = useState(false);

  const draftTimer = useRef<number | null>(null);
  const draftRef = useRef<Record<string, unknown>>({});
  const bookingVersionRef = useRef<string | null>(null);
  const proposalVersionRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  const flushInFlight = useRef<Promise<void> | null>(null);
  const unmountedRef = useRef(false);
  const availabilitySeq = useRef(0);
  /** Bumps on every local composer reset so stale async load/flush/availability settle cannot re-apply. */
  const composerEpochRef = useRef(0);
  const bookingStatusRef = useRef<string | null>(null);
  const successRef = useRef<string | null>(null);
  const bookingQueryIdRef = useRef(bookingQueryId);
  const siteIdRef = useRef(siteId);
  const selectedDateRef = useRef(selectedDate);
  siteIdRef.current = siteId;
  selectedDateRef.current = selectedDate;
  bookingQueryIdRef.current = bookingQueryId;
  bookingStatusRef.current = booking?.status ?? null;
  successRef.current = success;

  const holdSourceExpires =
    editMode
      ? (proposal?.hold_expires_at ?? null)
      : (booking?.hold_expires_at ?? null);
  const holdActive =
    !editMode &&
    booking !== null &&
    (booking.status === 'HELD' || booking.status === 'DRAFT') &&
    booking.hold_expires_at !== null;
  const editHoldActive =
    editMode && proposal !== null && proposal.hold_expires_at !== null;
  const now = useHoldTicker(holdActive || editHoldActive);

  const siteName = props.catalogSites.find((s) => s.id === siteId)?.display_name ?? '';
  const equipmentName =
    props.catalogEquipment.find((e) => e.id === equipmentId)?.display_name ?? '';

  useEffect(() => {
    // StrictMode (dev) remounts: cleanup sets true; reset so availability can settle.
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (draftTimer.current !== null) {
        window.clearTimeout(draftTimer.current);
        draftTimer.current = null;
      }
    };
  }, []);

  const clearDraftTimer = () => {
    if (draftTimer.current !== null) {
      window.clearTimeout(draftTimer.current);
      draftTimer.current = null;
    }
  };

  /**
   * Safe blank composer after a confirmed submit (or when leaving a viewed booking
   * via «Новая запись»). Does not cancel the confirmed booking on the server and
   * does not create a HOLD until the user picks a slot again.
   */
  const startFreshBooking = useCallback((mode: 'push' | 'replace' = 'replace') => {
    composerEpochRef.current += 1;
    availabilitySeq.current += 1;
    clearDraftTimer();
    dirtyRef.current = false;
    flushInFlight.current = null;
    bookingVersionRef.current = null;
    proposalVersionRef.current = null;
    draftRef.current = {};
    bookingQueryIdRef.current = null;
    bookingStatusRef.current = null;
    successRef.current = null;
    const today = ymdInEkb(new Date());
    setBookingQueryId(null);
    setProposalQueryId(null);
    setSiteId('');
    setEquipmentId('');
    setSelectedDate(today);
    setViewMonth(monthKeyFromYmd(today));
    setSlots([]);
    setSelectedSlot(null);
    setSlotsLoading(false);
    setBooking(null);
    setProposal(null);
    setForm(null);
    setReuseContext(null);
    setDraft({});
    setBusy(false);
    setError(null);
    setActionError(null);
    setFieldErrors({});
    setSuccess(null);
    setStaleHint(false);
    navigate('/booking', mode);
  }, []);

  useEffect(() => {
    return subscribePortalPath(() => {
      const nextBooking = readQueryParam('booking');
      const nextProposal = readQueryParam('proposal');
      const previousBooking = bookingQueryIdRef.current;
      bookingQueryIdRef.current = nextBooking;
      setBookingQueryId(nextBooking);
      setProposalQueryId(nextProposal);
      // Top nav «Новая запись» (and any clean /booking navigation) while the page
      // still shows a confirmed composer: reset without full remount/reload.
      if (
        window.location.pathname === '/booking' &&
        nextBooking === null &&
        nextProposal === null &&
        (previousBooking !== null ||
          bookingStatusRef.current === 'CONFIRMED' ||
          successRef.current === 'submitted')
      ) {
        startFreshBooking('replace');
      }
    });
  }, [startFreshBooking]);

  const fieldLabelMap = (): Readonly<Record<string, string>> => {
    const map: Record<string, string> = {};
    if (form === null) {
      return map;
    }
    for (const field of form.fields) {
      map[field.field_key] = field.label;
    }
    for (const doc of form.document_requirements) {
      const identity = `${doc.document_type_id}::${doc.subject_binding}`;
      for (const meta of doc.metadata_fields) {
        map[`${identity}:${meta.field_code}`] = `${doc.display_label}: ${meta.label}`;
        map[meta.field_code] = meta.label;
      }
    }
    return map;
  };

  const focusFirstInvalidField = (errors: Readonly<Record<string, string>>) => {
    const firstKey = Object.keys(errors)[0];
    if (firstKey === undefined) {
      return;
    }
    window.requestAnimationFrame(() => {
      const safeKey = firstKey.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const el = document.querySelector<HTMLElement>(`[data-native-field="${safeKey}"]`);
      if (el !== null) {
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        el.focus();
      }
    });
  };

  const reportFailure = (failure: unknown, options?: { nearAction?: boolean }) => {
    const labels = fieldLabelMap();
    const mapped = nativeFieldErrors(failure, labels);
    setFieldErrors(mapped);
    if (isNativeValidationError(failure)) {
      const summary = nativeValidationSummary(failure, labels);
      if (options?.nearAction === true) {
        setActionError(summary);
        setError(null);
      } else {
        setError(summary);
        setActionError(null);
      }
      focusFirstInvalidField(mapped);
      return;
    }
    setError(nativeProblemMessage(failure));
    setActionError(null);
    if (isNativeStaleConflict(failure)) {
      setStaleHint(true);
    }
  };

  const loadAvailability = useCallback(async () => {
    if (siteId === '') {
      setSlots([]);
      setSlotsLoading(false);
      return;
    }
    const seq = ++availabilitySeq.current;
    const requestSite = siteId;
    const requestDate = selectedDate;
    setSlotsLoading(true);
    try {
      const body = await props.api.availability(requestSite, requestDate);
      // Drop stale responses when site/date changed while the request was in flight.
      if (
        seq !== availabilitySeq.current ||
        requestSite !== siteIdRef.current ||
        requestDate !== selectedDateRef.current ||
        unmountedRef.current
      ) {
        return;
      }
      setSlots(body.slots);
    } catch (failure) {
      if (
        seq !== availabilitySeq.current ||
        requestSite !== siteIdRef.current ||
        requestDate !== selectedDateRef.current ||
        unmountedRef.current
      ) {
        return;
      }
      reportFailure(failure);
    } finally {
      if (seq === availabilitySeq.current && !unmountedRef.current) {
        setSlotsLoading(false);
      }
    }
  }, [props.api, selectedDate, siteId]);

  useEffect(() => {
    void loadAvailability();
  }, [loadAvailability]);

  const applySelectedYmd = (ymd: string) => {
    setSelectedDate(ymd);
    setViewMonth(monthKeyFromYmd(ymd));
  };

  const bindFromForm = (formBody: NativeFormResponse, proposalId: string | null) => {
    const ep = formBody.edit_proposal;
    const rawDraft =
      proposalId !== null && ep !== null ? (ep.draft ?? {}) : (formBody.draft ?? {});
    const typedDraft = normalizeDraftTypes(
      rawDraft,
      formBody.fields,
      formBody.document_requirements,
    );
    if (proposalId !== null && ep !== null) {
      setProposal(ep);
      proposalVersionRef.current = ep.proposal_version;
      setSiteId(ep.inspection_site_id);
      setEquipmentId(ep.equipment_type_id);
      setSelectedSlot(ep.slot_start_at);
      applySelectedYmd(ymdInEkb(new Date(ep.slot_start_at)));
    } else {
      setProposal(null);
      proposalVersionRef.current = null;
      const row = formBody.booking;
      setSiteId(row.inspection_site_id);
      setEquipmentId(row.equipment_type_id);
      setSelectedSlot(row.slot_start_at);
      applySelectedYmd(ymdInEkb(new Date(row.slot_start_at)));
    }
    setDraft(typedDraft);
    draftRef.current = typedDraft;
    setBooking(formBody.booking);
    bookingVersionRef.current = formBody.booking.version;
    setForm(formBody);
    setReuseContext(formBody.reuse_context ?? null);
  };

  const applyReuseSelection = async (input: {
    readonly selectedContractorProfileId: string | null;
    readonly selectedVehicleProfileId: string | null;
  }) => {
    if (booking === null || formDisabled) {
      return;
    }
    try {
      const result = await props.api.selectProfiles(booking.id, {
        expected_version: booking.version,
        selected_contractor_profile_id: input.selectedContractorProfileId ?? 'NEW',
        selected_vehicle_profile_id: input.selectedVehicleProfileId ?? 'NEW',
      });
      setReuseContext(result.reuse_context);
      setForm((prev) =>
        prev === null ? prev : { ...prev, reuse_context: result.reuse_context },
      );
      bookingVersionRef.current = result.booking_version;
      setBooking((prev) =>
        prev === null ? prev : { ...prev, version: result.booking_version },
      );
    } catch (failure) {
      reportFailure(failure, { nearAction: true });
    }
  };

  const loadExisting = useCallback(
    async (bookingId: string, proposalId: string | null) => {
      const epoch = composerEpochRef.current;
      setBusy(true);
      setError(null);
      setStaleHint(false);
      try {
        const formBody = await props.api.getForm(bookingId, proposalId ?? undefined);
        if (epoch !== composerEpochRef.current || unmountedRef.current) {
          return;
        }
        bindFromForm(formBody, proposalId);
        setFieldErrors({});
      } catch (failure) {
        if (epoch !== composerEpochRef.current || unmountedRef.current) {
          return;
        }
        reportFailure(failure);
      } finally {
        if (epoch === composerEpochRef.current && !unmountedRef.current) {
          setBusy(false);
        }
      }
    },
    [props.api],
  );

  useEffect(() => {
    if (bookingQueryId !== null) {
      void loadExisting(bookingQueryId, proposalQueryId);
    }
  }, [bookingQueryId, proposalQueryId, loadExisting]);

  const flushDraft = useCallback(async (): Promise<void> => {
    clearDraftTimer();
    if (flushInFlight.current !== null) {
      await flushInFlight.current;
      // Another edit may have dirtied the draft while the prior flush was in flight.
      if (dirtyRef.current) {
        await flushDraft();
      }
      return;
    }
    if (!dirtyRef.current) {
      return;
    }
    const bookingId = booking?.id;
    if (bookingId === undefined) {
      return;
    }
    const epoch = composerEpochRef.current;
    const run = (async () => {
      try {
        // Loop until the latest snapshot is persisted (covers rapid successive edits).
        while (
          dirtyRef.current &&
          !unmountedRef.current &&
          epoch === composerEpochRef.current
        ) {
          const typed = normalizeDraftTypes(
            draftRef.current,
            form?.fields ?? [],
            form?.document_requirements ?? [],
          );
          draftRef.current = typed;
          if (!unmountedRef.current && epoch === composerEpochRef.current) {
            setDraft(typed);
          }
          const snapshot = typed;
          dirtyRef.current = false;
          if (editMode && proposal !== null) {
            const expected = proposalVersionRef.current ?? proposal.proposal_version;
            const updated = await props.api.patchEditProposal(bookingId, proposal.id, {
              expected_proposal_version: expected,
              draft: snapshot,
            });
            if (!unmountedRef.current && epoch === composerEpochRef.current) {
              setBooking(updated.booking);
              setProposal(updated.edit_proposal);
              bookingVersionRef.current = updated.booking.version;
              proposalVersionRef.current = updated.edit_proposal.proposal_version;
            }
          } else if (!editMode && booking !== null && booking.status !== 'CONFIRMED') {
            const expected = bookingVersionRef.current ?? booking.version;
            const updated = await props.api.patchDraft(bookingId, snapshot, expected);
            if (!unmountedRef.current && epoch === composerEpochRef.current) {
              setBooking(updated);
              bookingVersionRef.current = updated.version;
            }
          }
        }
      } catch (failure) {
        if (epoch !== composerEpochRef.current) {
          return;
        }
        dirtyRef.current = true;
        if (!unmountedRef.current) {
          reportFailure(failure);
        }
        throw failure;
      } finally {
        flushInFlight.current = null;
      }
    })();
    flushInFlight.current = run;
    await run;
  }, [booking, editMode, form, proposal, props.api]);

  const scheduleDraftSave = (nextDraft: Record<string, unknown>) => {
    draftRef.current = nextDraft;
    dirtyRef.current = true;
    clearDraftTimer();
    draftTimer.current = window.setTimeout(() => {
      void flushDraft().catch(() => undefined);
    }, 450);
  };

  const applyLocalDraft = (next: Record<string, unknown>) => {
    setDraft(next);
    scheduleDraftSave(next);
  };

  const refreshFormAfterConfigChange = async (bookingId: string, proposalId: string | null) => {
    const formBody = await props.api.getForm(bookingId, proposalId ?? undefined);
    setForm(formBody);
    const raw =
      proposalId !== null && formBody.edit_proposal !== null
        ? (formBody.edit_proposal.draft ?? {})
        : (formBody.draft ?? {});
    const typed = normalizeDraftTypes(raw, formBody.fields, formBody.document_requirements);
    if (proposalId !== null && formBody.edit_proposal !== null) {
      setProposal(formBody.edit_proposal);
      proposalVersionRef.current = formBody.edit_proposal.proposal_version;
    }
    setDraft(typed);
    draftRef.current = typed;
  };

  const holdSlot = async (slotStart: string) => {
    if (siteId === '' || equipmentId === '') {
      setError('Выберите площадку и вид техники.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      if (editMode && booking !== null && proposal !== null) {
        await flushDraft().catch(() => undefined);
        const updated = await props.api.patchEditProposal(booking.id, proposal.id, {
          expected_proposal_version: proposalVersionRef.current ?? proposal.proposal_version,
          inspection_site_id: siteId,
          equipment_type_id: equipmentId,
          slot_start_at: slotStart,
          draft: draftRef.current,
        });
        setBooking(updated.booking);
        setProposal(updated.edit_proposal);
        bookingVersionRef.current = updated.booking.version;
        proposalVersionRef.current = updated.edit_proposal.proposal_version;
        setSelectedSlot(slotStart);
        dirtyRef.current = false;
        await refreshFormAfterConfigChange(booking.id, proposal.id);
      } else {
        const row = await props.api.createHold({
          inspection_site_id: siteId,
          equipment_type_id: equipmentId,
          slot_start_at: slotStart,
          booking_id: booking?.id,
          expected_version: bookingVersionRef.current ?? booking?.version,
          idempotencyKey: crypto.randomUUID(),
        });
        setBooking(row);
        bookingVersionRef.current = row.version;
        setSelectedSlot(slotStart);
        dirtyRef.current = false;
        await refreshFormAfterConfigChange(row.id, null);
      }
    } catch (failure) {
      reportFailure(failure);
      await loadAvailability();
    } finally {
      setBusy(false);
    }
  };

  /** Site/equipment change: re-HOLD (create) or proposal PATCH; never touch confirmed claim in edit. */
  const onSiteOrEquipmentChange = async (nextSite: string, nextEquipment: string) => {
    setSiteId(nextSite);
    setEquipmentId(nextEquipment);
    setSelectedSlot(null);
    setFieldErrors({});
    if (booking === null) {
      return;
    }
    if (editMode && proposal !== null) {
      // Keep current proposal slot until user picks a new one; still patch site/type so form refreshes.
      setBusy(true);
      setError(null);
      try {
        await flushDraft().catch(() => undefined);
        const updated = await props.api.patchEditProposal(booking.id, proposal.id, {
          expected_proposal_version: proposalVersionRef.current ?? proposal.proposal_version,
          inspection_site_id: nextSite,
          equipment_type_id: nextEquipment,
          draft: draftRef.current,
        });
        setBooking(updated.booking);
        setProposal(updated.edit_proposal);
        proposalVersionRef.current = updated.edit_proposal.proposal_version;
        setSelectedSlot(updated.edit_proposal.slot_start_at);
        applySelectedYmd(ymdInEkb(new Date(updated.edit_proposal.slot_start_at)));
        dirtyRef.current = false;
        await refreshFormAfterConfigChange(booking.id, proposal.id);
      } catch (failure) {
        reportFailure(failure);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (booking.status === 'CONFIRMED') {
      return;
    }
    // New-booking path: clear form UI; draft retained in draftRef until re-HOLD on new slot.
    setForm(null);
  };

  const submitBooking = async () => {
    if (editMode || booking === null || form === null || booking.status === 'CONFIRMED') {
      return;
    }
    setBusy(true);
    setError(null);
    setActionError(null);
    setFieldErrors({});
    setStaleHint(false);
    try {
      // Ensure persisted string numerics are coerced before final submit CAS.
      const typed = normalizeDraftTypes(draftRef.current, form.fields, form.document_requirements);
      draftRef.current = typed;
      setDraft(typed);
      dirtyRef.current = true;
      await flushDraft();
      const version = bookingVersionRef.current ?? booking.version;
      const updated = await props.api.submit(booking.id, {
        expected_version: version,
        idempotencyKey: crypto.randomUUID(),
        configuration_plan_revision_id: form.configuration.plan_revision_id,
        configuration_fingerprint: form.configuration.configuration_fingerprint,
      });
      setBooking(updated);
      bookingVersionRef.current = updated.version;
      dirtyRef.current = false;
      setActionError(null);
      setSuccess('submitted');
    } catch (failure) {
      reportFailure(failure, { nearAction: true });
    } finally {
      setBusy(false);
    }
  };

  const applyEdit = async () => {
    if (!editMode || booking === null || proposal === null || form === null) {
      return;
    }
    if (booking.current_inspection_case_id === null) {
      setError('Нет активного дела для сохранения изменений.');
      return;
    }
    setBusy(true);
    setError(null);
    setActionError(null);
    setFieldErrors({});
    setStaleHint(false);
    try {
      const typed = normalizeDraftTypes(draftRef.current, form.fields, form.document_requirements);
      draftRef.current = typed;
      setDraft(typed);
      dirtyRef.current = true;
      await flushDraft();
      const updated = await props.api.applyEditProposal(booking.id, proposal.id, {
        expected_version: bookingVersionRef.current ?? booking.version,
        expected_case_id: booking.current_inspection_case_id,
        expected_proposal_version: proposalVersionRef.current ?? proposal.proposal_version,
        idempotencyKey: crypto.randomUUID(),
        configuration_plan_revision_id: form.configuration.plan_revision_id,
        configuration_fingerprint: form.configuration.configuration_fingerprint,
      });
      setBooking(updated);
      bookingVersionRef.current = updated.version;
      setProposal(null);
      proposalVersionRef.current = null;
      dirtyRef.current = false;
      setActionError(null);
      setSuccess('applied');
      // Drop proposal from URL → editMode becomes false via path subscription.
      navigate(`/booking?booking=${encodeURIComponent(updated.id)}`, 'replace');
      setProposalQueryId(null);
    } catch (failure) {
      reportFailure(failure, { nearAction: true });
    } finally {
      setBusy(false);
    }
  };

  const abandonEdit = async () => {
    if (!editMode || booking === null || proposal === null) {
      return;
    }
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      clearDraftTimer();
      dirtyRef.current = false;
      await props.api.abandonEditProposal(booking.id, proposal.id);
      setProposal(null);
      proposalVersionRef.current = null;
      setProposalQueryId(null);
      setSuccess('abandoned');
      navigate(`/my`);
    } catch (failure) {
      reportFailure(failure);
    } finally {
      setBusy(false);
    }
  };

  const holdExpired =
    holdSourceExpires !== null &&
    new Date(holdSourceExpires).getTime() <= now &&
    (!editMode ? booking?.status !== 'CONFIRMED' : true);

  const bookingConfirmed = !editMode && booking?.status === 'CONFIRMED';
  const holdSummaryTitle = bookingConfirmed
    ? 'Статус заявки'
    : editMode
      ? 'Резерв правки'
      : 'Временный резерв';
  const holdSummaryDetail = bookingConfirmed
    ? 'Подтверждена'
    : holdSourceExpires === null
      ? 'Нет активного резерва'
      : 'Действует 30 минут';
  const holdSummaryClock = bookingConfirmed
    ? 'Подтверждено'
    : formatHoldCountdown(holdSourceExpires, now);

  const formDisabled =
    busy ||
    holdExpired ||
    (!editMode && booking?.status === 'CONFIRMED') ||
    (editMode && booking !== null && !booking.editable && proposal === null);

  const slotDisabled =
    busy ||
    (!editMode && booking?.status === 'CONFIRMED') ||
    (editMode && booking !== null && !booking.editable && success !== null);

  return (
    <div className="native-booking-page">
      <div className="native-page-head">
        <div>
          <h1>{editMode ? 'Изменение заявки' : 'Запись на осмотр техники'}</h1>
          <p>
            {editMode
              ? 'Можно изменить площадку, вид техники, время и анкету. Исходная заявка сохранится до нажатия «Сохранить изменения».'
              : 'Выберите площадку и время, заполните анкету и отправьте заявку. Всё на одной странице.'}
          </p>
        </div>
      </div>
      {error !== null ? (
        <p role="alert" className="native-banner native-banner-danger">
          {error}
        </p>
      ) : null}
      {staleHint ? (
        <p role="status" className="native-banner native-banner-warn">
          Данные могли устареть.{' '}
          <button
            type="button"
            className="native-link-btn"
            onClick={() => {
              if (booking !== null) {
                void loadExisting(booking.id, editMode ? proposal?.id ?? proposalQueryId : null);
              }
            }}
          >
            Обновить с сервера
          </button>
        </p>
      ) : null}
      <div className="native-booking-layout">
        <div className="native-booking-main">
          <section className="native-panel">
            <header className="native-panel-header">
              <span className="native-section-no">01</span>
              <div>
                <h2>Место и тип техники</h2>
                <p className="native-subtitle">Определяют расписание и состав анкеты</p>
              </div>
            </header>
            <div className="native-panel-body native-two">
              <label className="native-field">
                Площадка осмотра <span className="native-required">*</span>
                <select
                  className="native-input"
                  value={siteId}
                  disabled={slotDisabled}
                  onChange={(event) => {
                    void onSiteOrEquipmentChange(event.target.value, equipmentId);
                  }}
                >
                  <option value="">Выберите площадку</option>
                  {props.catalogSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="native-field">
                Вид ТС / спецтехники <span className="native-required">*</span>
                <select
                  className="native-input"
                  value={equipmentId}
                  disabled={slotDisabled}
                  onChange={(event) => {
                    void onSiteOrEquipmentChange(siteId, event.target.value);
                  }}
                >
                  <option value="">Выберите вид техники</option>
                  {props.catalogEquipment.map((eq) => (
                    <option key={eq.id} value={eq.id}>
                      {eq.display_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className="native-panel">
            <header className="native-panel-header">
              <span className="native-section-no">02</span>
              <div>
                <h2>Дата и время</h2>
                <p className="native-subtitle">Слоты по 60 минут · Екатеринбург (UTC+05:00)</p>
              </div>
            </header>
            <div className="native-panel-body">
              {holdExpired && !editMode ? (
                <p className="native-banner native-banner-warn">
                  Время больше не удерживается. Анкета сохранена — выберите новый слот.
                </p>
              ) : null}
              {holdExpired && editMode ? (
                <p className="native-banner native-banner-warn">
                  Резерв правки по времени истёк. Исходная заявка не изменена — выберите слот снова.
                </p>
              ) : null}
              <div className="native-calendar-wrap">
                <NativeMonthCalendar
                  selectedDate={selectedDate}
                  viewMonth={viewMonth}
                  canSelectDays={siteId !== '' && !slotDisabled && !busy}
                  navigationDisabled={busy}
                  onViewMonthChange={setViewMonth}
                  onSelectDate={(ymd) => {
                    applySelectedYmd(ymd);
                    setSelectedSlot(null);
                    setSlots([]);
                  }}
                />
                <div className="native-slots-panel">
                  <h4 className="native-slots-title">
                    {siteId === ''
                      ? 'Выберите площадку'
                      : formatDayLongRu(selectedDate)}
                  </h4>
                  <p className="native-slots-sub">Свободное время для выбранной площадки · 60 мин</p>
                  {slotsLoading ? (
                    <p className="native-muted" data-testid="native-slots-loading">
                      Загрузка слотов…
                    </p>
                  ) : null}
                  <div className="native-slot-grid" data-testid="native-slot-grid">
                    {slots.map((slot) => (
                      <button
                        key={slot.start_at}
                        type="button"
                        className={
                          selectedSlot === slot.start_at
                            ? 'native-slot selected'
                            : slot.available || selectedSlot === slot.start_at
                              ? 'native-slot'
                              : 'native-slot disabled'
                        }
                        disabled={(!slot.available && selectedSlot !== slot.start_at) || slotDisabled}
                        onClick={() => {
                          void holdSlot(slot.start_at);
                        }}
                      >
                        {slotLocalTime(slot.start_at)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="native-slot-legend">
                <span>Свободно</span>
                <span className="occupied">Занято / недоступно</span>
                <span className="picked">Ваш выбор</span>
              </div>
            </div>
          </section>

          <section className="native-panel">
            <header className="native-panel-header">
              <span className="native-section-no">03</span>
              <div>
                <h2>Анкета для осмотра</h2>
                <p className="native-subtitle">Заполняется после выбора времени</p>
              </div>
            </header>
            <div className="native-panel-body">
              {form === null ? (
                <p className="native-muted">Выберите свободный слот — здесь появится анкета.</p>
              ) : (
                <>
                {form.configuration.freshness === 'CONFIGURATION_REFRESH_REQUIRED' ? (
                  <p
                    className="native-banner native-banner-warn"
                    role="status"
                    id="native-freshness-banner"
                    data-testid="native-freshness-banner"
                  >
                    Каталог шаблонов обновился после оформления этой заявки. Ниже показана
                    сохранённая анкета по версии на момент подтверждения (поля и значения не
                    подменяются текущим каталогом). Для изменения заявки до начала осмотра
                    потребуется явная перепроверка конфигурации.
                    {typeof form.configuration.refresh_reason === 'string' &&
                    form.configuration.refresh_reason.trim() !== ''
                      ? ` ${form.configuration.refresh_reason}`
                      : ''}
                  </p>
                ) : null}
                <NativeReuseProfiles
                  reuseContext={reuseContext}
                  disabled={formDisabled}
                  onSelect={(selection) => {
                    void applyReuseSelection(selection);
                  }}
                />
                <NativeVerticalForm
                  fields={form.fields}
                  documents={form.document_requirements}
                  draft={draft}
                  disabled={formDisabled}
                  api={formDisabled ? null : props.api}
                  fieldErrors={fieldErrors}
                  onFieldChange={(fieldKey, value) => {
                    setFieldErrors((prev) => {
                      if (prev[fieldKey] === undefined) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next[fieldKey];
                      return next;
                    });
                    setActionError(null);
                    const valueType =
                      form.fields.find((f) => f.field_key === fieldKey)?.value_type ?? 'STRING';
                    applyLocalDraft(buildDraftPatch(draft, fieldKey, value, valueType));
                  }}
                  onFieldsChange={(patch) => {
                    setFieldErrors((prev) => {
                      let changed = false;
                      const next = { ...prev };
                      for (const key of Object.keys(patch)) {
                        if (next[key] !== undefined) {
                          delete next[key];
                          changed = true;
                        }
                      }
                      return changed ? next : prev;
                    });
                    setActionError(null);
                    let next = draft;
                    for (const [key, value] of Object.entries(patch)) {
                      const valueType =
                        form.fields.find((f) => f.field_key === key)?.value_type ?? 'STRING';
                      next = buildDraftPatch(next, key, value, valueType);
                    }
                    applyLocalDraft(next);
                  }}
                  onPlateFormatChange={(fieldKey, selection) => {
                    setFieldErrors((prev) => {
                      if (prev[fieldKey] === undefined) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next[fieldKey];
                      return next;
                    });
                    setActionError(null);
                    // Keep plate string; only persist runtime type for this field/booking.
                    applyLocalDraft(withNativePlateFormatChoice(draft, fieldKey, selection));
                  }}
                  onDocumentFieldChange={(documentTypeId, subjectBinding, fieldCode, value) => {
                    setActionError(null);
                    const doc = form.document_requirements.find(
                      (d) =>
                        d.document_type_id === documentTypeId && d.subject_binding === subjectBinding,
                    );
                    const valueType =
                      doc?.metadata_fields.find((m) => m.field_code === fieldCode)?.value_type ??
                      'STRING';
                    applyLocalDraft(
                      buildDocumentDraftPatch(
                        draft,
                        documentTypeId,
                        subjectBinding,
                        fieldCode,
                        value,
                        valueType,
                      ),
                    );
                  }}
                />
                </>
              )}
            </div>
          </section>

          <div className="native-sendbar">
            {editMode ? (
              <>
                <div>
                  <h3>Сохранить изменения?</h3>
                  <p>До сохранения действует исходная подтверждённая заявка.</p>
                  {actionError !== null ? (
                    <p role="alert" className="native-sendbar-error" data-testid="native-action-error">
                      {actionError}
                    </p>
                  ) : null}
                  {success === 'applied' ? (
                    <div
                      role="status"
                      className="native-sendbar-success"
                      data-testid="native-sendbar-success"
                    >
                      <p>
                        {booking?.public_number !== undefined && booking.public_number !== ''
                          ? `Изменения по заявке № ${booking.public_number} сохранены. Номер заявки не изменился.`
                          : 'Изменения сохранены. Номер заявки не изменился.'}
                      </p>
                      {selectedSlot !== null || siteName !== '' ? (
                        <p className="native-sendbar-success-meta">
                          {[
                            selectedSlot !== null ? formatEkbDateTime(selectedSlot) : null,
                            siteName !== '' ? siteName : null,
                          ]
                            .filter((part): part is string => part !== null)
                            .join(' · ')}
                        </p>
                      ) : null}
                      <button
                        type="button"
                        className="native-link-btn"
                        onClick={() => navigate('/my')}
                      >
                        Мои заявки
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="native-sendbar-actions">
                  <button
                    type="button"
                    className="native-btn native-btn-secondary"
                    disabled={busy}
                    onClick={() => {
                      void abandonEdit();
                    }}
                  >
                    Отменить правки
                  </button>
                  <button
                    type="button"
                    className="native-btn native-btn-primary"
                    disabled={
                      busy ||
                      form === null ||
                      booking === null ||
                      holdExpired ||
                      success === 'applied'
                    }
                    onClick={() => {
                      void applyEdit();
                    }}
                  >
                    Сохранить изменения
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <h3>
                    {success === 'applied'
                      ? 'Изменения сохранены'
                      : success === 'submitted' || booking?.status === 'CONFIRMED'
                        ? 'Заявка отправлена'
                        : 'Готово к отправке?'}
                  </h3>
                  {success !== 'submitted' &&
                  success !== 'applied' &&
                  booking?.status !== 'CONFIRMED' ? (
                    <p>Отправка подтверждает запись и передаёт данные инспекторам.</p>
                  ) : null}
                  {actionError !== null ? (
                    <p role="alert" className="native-sendbar-error" data-testid="native-action-error">
                      {actionError}
                    </p>
                  ) : null}
                  {success === 'applied' ||
                  success === 'submitted' ||
                  (booking?.status === 'CONFIRMED' && success !== 'abandoned') ? (
                    <div
                      role="status"
                      className="native-sendbar-success"
                      data-testid="native-sendbar-success"
                    >
                      <p>
                        {success === 'applied'
                          ? booking?.public_number !== undefined && booking.public_number !== ''
                            ? `Изменения по заявке № ${booking.public_number} сохранены. Номер заявки не изменился.`
                            : 'Изменения сохранены. Номер заявки не изменился.'
                          : booking?.public_number !== undefined && booking.public_number !== ''
                            ? `Заявка № ${booking.public_number} отправлена / подтверждена`
                            : 'Заявка отправлена / подтверждена'}
                      </p>
                      {selectedSlot !== null || siteName !== '' ? (
                        <p className="native-sendbar-success-meta">
                          {[
                            selectedSlot !== null ? formatEkbDateTime(selectedSlot) : null,
                            siteName !== '' ? siteName : null,
                          ]
                            .filter((part): part is string => part !== null)
                            .join(' · ')}
                        </p>
                      ) : null}
                      <button
                        type="button"
                        className="native-link-btn"
                        onClick={() => navigate('/my')}
                      >
                        Мои заявки
                      </button>
                    </div>
                  ) : null}
                </div>
                {booking?.status === 'CONFIRMED' || success === 'submitted' ? (
                  <div className="native-sendbar-nav" data-testid="native-sendbar-post-submit">
                    <button
                      type="button"
                      className="native-btn native-btn-primary"
                      data-testid="native-create-new-booking"
                      onClick={() => {
                        startFreshBooking('push');
                      }}
                    >
                      Создать новую заявку
                    </button>
                    <button
                      type="button"
                      className="native-btn native-btn-secondary"
                      data-testid="native-goto-my-bookings"
                      onClick={() => navigate('/my')}
                    >
                      Мои заявки
                    </button>
                    {booking !== null ? (
                      <button
                        type="button"
                        className="native-btn native-btn-secondary"
                        data-testid="native-view-booking"
                        onClick={() =>
                          navigate(`/booking?booking=${encodeURIComponent(booking.id)}`)
                        }
                      >
                        Просмотреть заявку
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="native-btn native-btn-primary"
                    data-testid="native-submit-booking"
                    disabled={busy || form === null || booking === null || holdExpired}
                    onClick={() => {
                      void submitBooking();
                    }}
                  >
                    Отправить заявку на осмотр
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <aside className="native-summary-column">
          <div className="native-summary-card">
            <div className="native-summary-head">
              <span>Ваша заявка</span>
              <h3>Параметры записи</h3>
            </div>
            <div className="native-summary-body">
              <div className="native-summary-item">
                <span className="native-summary-key">Площадка</span>
                <strong>{siteName === '' ? 'Не выбрана' : siteName}</strong>
              </div>
              <div className="native-summary-item">
                <span className="native-summary-key">Вид техники</span>
                <strong>{equipmentName === '' ? 'Не выбран' : equipmentName}</strong>
              </div>
              <div className="native-summary-item">
                <span className="native-summary-key">Дата и время</span>
                <strong>
                  {selectedSlot === null ? 'Не выбрано' : formatEkbDateTime(selectedSlot)}
                </strong>
              </div>
              <div
                className={
                  holdExpired
                    ? 'native-hold-box expired'
                    : !editMode && booking?.status === 'CONFIRMED'
                      ? 'native-hold-box confirmed'
                      : 'native-hold-box'
                }
                data-testid="native-hold-summary"
              >
                <div className="native-hold-meta">
                  <b>{holdSummaryTitle}</b>
                  <span>{holdSummaryDetail}</span>
                </div>
                <div className="native-hold-clock">{holdSummaryClock}</div>
              </div>
              {booking?.public_number !== undefined ? (
                <p className="native-muted">№ {booking.public_number}</p>
              ) : null}
            </div>
          </div>
          <div className="native-hint-box">
            <b>Как это работает</b>
            <p>
              Часовой слот — время для планирования приезда. Осмотр может начаться раньше или позже
              забронированного часа.
            </p>
          </div>
          {success !== null ? (
            <button type="button" className="native-btn native-btn-secondary" onClick={() => navigate('/my')}>
              Мои заявки
            </button>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
